import { FormEvent, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { StatusChip } from "@/components/StatusChip";
import { useLazyApi } from "@/hooks/useApi";
import { formatCurrency, formatDate, getFullUrl } from "@/lib/utils";
import { dealerService } from "@/services/DealerService";
import { DealerSearchResponse, DealerSearchResult } from "@/types/dealer";
import { BadgeCheck, ChevronDown, Globe, Phone } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

const displayValue = (value: string | null): string => value?.trim() || "-";

function SetupStatusChip({ status }: { status: string | null }) {
  const trimmed = status?.trim() || "";

  return <StatusChip status={trimmed} variant="pastel" fullWidth={true} />;
}

function TrustedBadge({ isTrusted }: { isTrusted: boolean }) {
  return (
    <BadgeCheck
      role="img"
      aria-label={isTrusted ? "Trusted" : "Not trusted"}
      className={`size-5 ${isTrusted ? "text-emerald-600 fill-emerald-100" : "text-slate-300"}`}
    />
  );
}

function DealerActions({ dealer }: { dealer: DealerSearchResult }) {
  const phoneNumbers = [dealer.dealerPhoneNumber1, dealer.dealerPhoneNumber2].filter(
    (phoneNumber): phoneNumber is string => Boolean(phoneNumber?.trim()),
  );

  return (
    <div className="flex min-w-20 items-center gap-1">
      {phoneNumbers.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="text-primary-700 hover:text-primary-700" aria-label={`Call ${dealer.dealerName}`}>
              <Phone className="size-4" />
              <ChevronDown className="size-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {phoneNumbers.map((phoneNumber) => (
              <DropdownMenuItem key={phoneNumber} asChild>
                <a href={`tel:${phoneNumber}`}>{phoneNumber}</a>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      {dealer.dealerUrl?.trim() && (
        <Button variant="ghost" size="icon" className="text-primary-700 hover:text-primary-700" asChild>
          <a
            href={getFullUrl(dealer.dealerUrl)}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Open ${dealer.dealerName} website`}
          >
            <Globe className="size-4" />
          </a>
        </Button>
      )}
      {!dealer.dealerUrl?.trim() && phoneNumbers.length === 0 && <span className="text-muted-foreground">-</span>}
    </div>
  );
}

export function DealerSearch() {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);
  const [result, setResult] = useState<DealerSearchResponse | null>(null);
  const searchController = useRef<AbortController | null>(null);
  const { execute, loading, error } = useLazyApi<DealerSearchResponse>();

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (loading) return;

    const filter = searchInput.trim();
    if (!filter) {
      setResult(null);
      setValidationError("Enter a Dealer ID, name or postcode to search.");
      return;
    }

    setValidationError(null);
    setResult(null);
    const controller = new AbortController();
    searchController.current = controller;
    const searchResult = await execute((signal) => dealerService.searchDealers(filter, AbortSignal.any([signal, controller.signal])));
    if (!controller.signal.aborted) setResult(searchResult);
  };

  const handleCancel = () => {
    searchController.current?.abort();
  };

  const hasError = validationError ?? error;
  const hasNoResults = result && (result.searchCount === 0 || result.searchResults.length === 0);

  return (
    <div className="flex h-full flex-col gap-4 px-4 py-3">
      <div>
        <h1 className="text-2xl font-semibold">Dealer Search</h1>
        <p className="text-muted-foreground text-sm">
          Search by Dealer ID, name or postcode to retrieve a list of associated dealers.
        </p>
      </div>

      <form className="flex max-w-xl items-end gap-3" onSubmit={handleSubmit}>
        <div className="flex flex-1 flex-col gap-1">
          <label className="text-sm font-medium" htmlFor="dealer-search-input">
            Dealer ID, name or postcode
          </label>
          <Input
            id="dealer-search-input"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="e.g. 33967, Test Dealer or BT1 1BT"
          />
        </div>
        <Button type="submit" disabled={loading}>
          {loading ? "Searching..." : "Search"}
        </Button>
        {loading && <Button type="button" variant="outline" onClick={handleCancel}>Cancel</Button>}
      </form>

      {hasError && (
        <div className="border-destructive text-destructive rounded-md border p-3 text-sm" role="alert">
          {hasError}
        </div>
      )}

      {hasNoResults && (
        <div className="rounded-md border p-3 text-sm">
          Sorry, no dealers with the search type {result.searchType} were found.
        </div>
      )}

      {result && !hasNoResults && (
        <section className="min-w-0 rounded-md border">
          <div className="border-b px-4 py-3">
            <h2 className="font-semibold">Search results ({result.searchResults.length})</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-max text-sm">
              <thead>
                <tr className="bg-muted/40 border-b text-left">
                  <th className="p-3 font-medium">Dealer ID</th>
                  <th className="p-3 font-medium">Name</th>
                  <th className="p-3 font-medium">Postcode</th>
                  <th className="p-3 font-medium">Setup status</th>
                  <th className="p-3 font-medium">Car search status</th>
                  <th className="p-3 font-medium">Intro status</th>
                  <th className="p-3 font-medium">Trusted</th>
                  <th className="p-3 font-medium">Last deal</th>
                  <th className="p-3 text-center font-medium">Deals</th>
                  <th className="p-3 text-center font-medium">Banned lenders</th>
                  <th className="p-3 font-medium">Admin fee</th>
                  <th className="p-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {result.searchResults.map((dealer) => (
                  <tr
                    key={dealer.dealerId}
                    className="hover:bg-muted/30 cursor-pointer border-b last:border-b-0"
                    onClick={() => navigate(`/dealer-search/${dealer.dealerId}`)}
                  >
                    <td className="p-3">{dealer.dealerId}</td>
                    <td className="p-3 font-semibold">
                      <Link
                        to={`/dealer-search/${dealer.dealerId}`}
                        className="text-primary-700 hover:underline focus-visible:underline focus-visible:outline-none"
                        onClick={(event) => event.stopPropagation()}
                      >
                        {dealer.dealerName}
                      </Link>
                    </td>
                    <td className="p-3">{displayValue(dealer.postcode)}</td>
                    <td className="p-3">
                      <SetupStatusChip status={dealer.dealerSetupStatus} />
                    </td>
                    <td className="p-3">{displayValue(dealer.dealerCarSearchStatus)}</td>
                    <td className="p-3">{displayValue(dealer.dealerIntroStatus)}</td>
                    <td className="p-3">
                      <TrustedBadge isTrusted={dealer.isTrusted} />
                    </td>
                    <td className="p-3">{dealer.lastDeal ? formatDate(dealer.lastDeal) : "-"}</td>
                    <td className="p-3 text-center">{dealer.dealsCount}</td>
                    <td className="p-3 text-center">{dealer.bannedLenders.length}</td>
                    <td className="p-3">{dealer.adminFee === null ? "-" : formatCurrency(dealer.adminFee)}</td>
                    <td
                      className="p-3"
                      onClick={(event) => event.stopPropagation()}
                    >
                      <DealerActions dealer={dealer} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}